import React, { useState } from "react";

import { useLocation, useNavigate, useTranslation } from "utils";

import { WEBSITE_LINK } from "consts";

import { Button, Drawer, ErrorBoundary, Icon, Link, SelectLanguage, Svg } from "components";

import { stepFromPath, trackSoilSightSignupClick } from "pages/app/soilsight-pre-login/utils/acquisitionTracking";

type AuthActionsProps = {
  className: string;
  // Desktop and drawer render the same actions, so their tracking ids are suffixed apart.
  suffix?: string;
  onBeforeNavigate?: () => void;
};

const AuthActions = ({ className, suffix = "", onBeforeNavigate }: AuthActionsProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goToSignUp = () => {
    trackSoilSightSignupClick(stepFromPath(pathname));
    onBeforeNavigate?.();
    navigate("/sign-up");
  };

  return (
    <div className={className}>
      <ErrorBoundary>
        <SelectLanguage skipRefresh placement="bottomRight" />
      </ErrorBoundary>
      <Link to="/sign-in" mixpanelText={`soilsight-pre-login-login${suffix}`} className="soilsight-pre-login__login" onClick={onBeforeNavigate}>
        {t("preLogin.header.login")}
      </Link>
      <Button type="primary" dataTestId={`soilsight-pre-login-register${suffix}`} className="soilsight-pre-login__register" onClick={goToSignUp}>
        {t("preLogin.header.signUp")}
      </Button>
    </div>
  );
};

export const PreLoginHeader = () => {
  const { t } = useTranslation("scenarioAnalysis");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const openMobileMenu = () => setIsMobileMenuOpen(true);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <header className="soilsight-pre-login__header">
      <nav className="soilsight-pre-login__nav" aria-label={t("preLogin.header.navLabel")}>
        <Link to={WEBSITE_LINK} mixpanelText="soilsight-pre-login-logo" target="_blank" className="soilsight-pre-login__logo">
          <Svg svgName="logos/klim_logo_text_black" width={75} height={32} />
        </Link>

        <AuthActions className="soilsight-pre-login__nav-actions soilsight-pre-login__nav-actions--desktop" />

        <button
          type="button"
          className="soilsight-pre-login__menu-btn"
          data-testid="soilsight-pre-login-menu"
          aria-label={t("preLogin.header.menuLabel")}
          onClick={openMobileMenu}
        >
          <Icon iconName="tabler/menu-2" size="m" />
        </button>
      </nav>

      <Drawer
        visible={isMobileMenuOpen}
        onClose={closeMobileMenu}
        placement="right"
        dataTestId="soilsight-pre-login-menu-drawer"
        title={t("preLogin.header.navLabel")}
      >
        <AuthActions className="soilsight-pre-login__menu-drawer-body" suffix="-mobile" onBeforeNavigate={closeMobileMenu} />
      </Drawer>
    </header>
  );
};
